import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Download, Upload, AlertCircle, FileUp, CheckCircle, XCircle, FileDown, Loader2 } from 'lucide-react';
import { toast } from '../../components/ui/toast';
import { api } from '../../services/api';
import { parseAndValidateCsv, generateExportCsv, downloadCsv, readFileAsText, SAMPLE_CSV_TEMPLATE, type CsvValidationResult } from '../../lib/csv';

export function BulkImportExport() {
  const { isSuperAdmin } = useAuth();
  const location = useLocation();
  const stateOrgId = (location.state as any)?.orgId || localStorage.getItem('lastSelectedOrgId');

  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');

  // Import State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsedResults, setParsedResults] = useState<CsvValidationResult[]>([]);
  const [headerErrors, setHeaderErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{ row: number; success: boolean; message: string }[] | null>(null);

  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) document.documentElement.classList.add('dark');
  }, []);

  if (!isSuperAdmin()) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only super administrators can access bulk operations.</AlertDescription>
        </Alert>
        <Link to="/dashboard">
          <Button variant="outline">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  if (!stateOrgId) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <Link to="/dashboard/users">
            <Button variant="ghost" className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </Link>
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Missing Organization Context</AlertTitle>
            <AlertDescription>Cannot perform bulk operations without an organization context. Please navigate from the user list.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // --- Handlers ---
  const handleDownloadTemplate = () => {
    downloadCsv(SAMPLE_CSV_TEMPLATE, 'users_import_template.csv');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setImportResults(null);
    setImportProgress(0);

    try {
      const content = await readFileAsText(selectedFile);
      const { results, headerErrors } = parseAndValidateCsv(content);
      setHeaderErrors(headerErrors);
      setParsedResults(results);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to read file', variant: 'destructive' });
    }
  };

  const handleImport = async () => {
    if (parsedResults.length === 0) return;
    setIsImporting(true);
    setImportProgress(0);
    setImportResults(null);

    const results: { row: number; success: boolean; message: string }[] = [];

    for (let i = 0; i < parsedResults.length; i++) {
      const parsed = parsedResults[i];
      if (!parsed.isValid) {
        results.push({ row: parsed.row, success: false, message: parsed.errors.join('; ') });
      } else {
        try {
          await api.post(`api/v1/organizations/${stateOrgId}/users`, {
            json: {
              email: parsed.data.email,
              password: parsed.data.password,
              name: parsed.data.name,
              role: parsed.data.role,
              roles: parsed.data.roles,
              send_verification_email: parsed.data.send_verification_email,
            }
          }).json();
          results.push({ row: parsed.row, success: true, message: 'Created successfully' });
        } catch (err: any) {
          results.push({ row: parsed.row, success: false, message: err.message || 'API Error' });
        }
      }
      setImportProgress(Math.round(((i + 1) / parsedResults.length) * 100));
    }

    setImportResults(results);
    setIsImporting(false);
    toast({ title: 'Import Complete', description: `Processed ${parsedResults.length} rows.` });
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    try {
      let allUsers: any[] = [];
      let page = 1;
      let hasNext = true;

      while (hasNext) {
        const response = await api.get(`api/v1/organizations/${stateOrgId}/users`, {
          searchParams: { page, limit: 50 },
        }).json() as any;

        const users = response.users || [];
        allUsers = [...allUsers, ...users];

        if (response.pagination && response.pagination.has_next) {
          page++;
          setExportProgress(Math.round((page / response.pagination.total_pages) * 100));
        } else {
          hasNext = false;
        }
      }

      setExportProgress(100);
      const csv = generateExportCsv(allUsers);
      downloadCsv(csv, `users_export_org_${stateOrgId}_${new Date().toISOString().split('T')[0]}.csv`);
      toast({ title: 'Export Complete', description: `Exported ${allUsers.length} users.` });
    } catch (err: any) {
      toast({ title: 'Export Failed', description: err.message || 'Failed to export users', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const validRowsCount = parsedResults.filter(r => r.isValid).length;
  const invalidRowsCount = parsedResults.filter(r => !r.isValid).length;
  const successfulImportsCount = importResults?.filter(r => r.success).length || 0;
  const failedImportsCount = importResults?.filter(r => !r.success).length || 0;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/users" state={{ orgId: stateOrgId }}>
            <Button variant="ghost" className="mr-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bulk Import / Export</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Manage users in bulk via CSV</p>
          </div>
        </div>
        <ThemeToggleCompact />
      </div>

      <div className="flex border-b mb-6 border-gray-200 dark:border-gray-800">
        <button
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'import'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('import')}
        >
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Import Users
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'export'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('export')}
        >
          <div className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export Users
          </div>
        </button>
      </div>

      {activeTab === 'import' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Download Template</CardTitle>
              <CardDescription>Start with our CSV template to ensure your data is formatted correctly.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <FileDown className="h-4 w-4 mr-2" />
                Download CSV Template
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Upload Data</CardTitle>
              <CardDescription>Upload your completed CSV file. We'll validate it before creating any users.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
                <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                  <FileUp className="h-4 w-4 mr-2" />
                  Select CSV File
                </Button>
                <span className="text-sm text-gray-500">{file ? file.name : 'No file selected'}</span>
              </div>

              {headerErrors.length > 0 && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Invalid File Header</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4 mt-2">
                      {headerErrors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {parsedResults.length > 0 && headerErrors.length === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>3. Validation & Import</CardTitle>
                <CardDescription>
                  Found {parsedResults.length} rows. {validRowsCount} valid, {invalidRowsCount} invalid.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Progress bar */}
                {(isImporting || importResults) && (
                  <div className="mb-6 space-y-2">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>Import Progress</span>
                      <span>{importProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                      <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${importProgress}%` }}></div>
                    </div>
                    {importResults && (
                      <div className="text-sm mt-2 flex gap-4">
                        <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4"/> {successfulImportsCount} Successful</span>
                        <span className="text-red-600 flex items-center gap-1"><XCircle className="h-4 w-4"/> {failedImportsCount} Failed</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="max-h-96 overflow-y-auto border rounded-md mb-4">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-800 dark:text-gray-300 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 border-b">Row</th>
                        <th className="px-4 py-2 border-b">Email</th>
                        <th className="px-4 py-2 border-b">Roles</th>
                        <th className="px-4 py-2 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedResults.map((r) => {
                        const importResult = importResults?.find(ir => ir.row === r.row);
                        
                        let statusContent;
                        if (importResult) {
                           statusContent = importResult.success ? (
                             <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3"/> Success</span>
                           ) : (
                             <span className="text-red-600 flex items-center gap-1" title={importResult.message}><XCircle className="h-3 w-3"/> Failed: {importResult.message}</span>
                           );
                        } else {
                           statusContent = r.isValid ? (
                             <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3"/> Valid</span>
                           ) : (
                             <span className="text-red-600 flex items-center gap-1" title={r.errors.join('; ')}><XCircle className="h-3 w-3"/> Invalid</span>
                           );
                        }

                        return (
                          <tr key={r.row} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50">
                            <td className="px-4 py-2 text-gray-500">{r.row}</td>
                            <td className="px-4 py-2 font-medium">{r.data.email}</td>
                            <td className="px-4 py-2">
                              {r.data.roles && r.data.roles.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {r.data.roles.map(role => (
                                    <Badge key={role} variant="secondary" className="text-[10px] py-0">{role}</Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2">{statusContent}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Button 
                  onClick={handleImport} 
                  disabled={isImporting || validRowsCount === 0 || (importResults !== null)}
                >
                  {isImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {importResults ? 'Import Complete' : `Import ${validRowsCount} Valid Users`}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'export' && (
        <Card>
          <CardHeader>
            <CardTitle>Export Organization Users</CardTitle>
            <CardDescription>
              Download a complete list of users in this organization as a CSV file.
              This includes all standard user fields. Passwords are not exported.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isExporting && (
              <div className="mb-6 space-y-2">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Export Progress (Fetching pages)</span>
                  <span>{exportProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                  <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${exportProgress}%` }}></div>
                </div>
              </div>
            )}
            <Button onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Export...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Generate CSV Export
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Quick inline Input component just for this file to avoid extra imports if it fails
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={
          "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 " + className
        }
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"
